// lib/models/need.ts
// ✅ UPDATED: Added 5-question matching fields
// TypeScript models matching the Go backend DTOs

// ==================== ACTIVE NEED TYPES ====================

// A contact person on a job posting (recruiting manager, HR, union rep).
export interface NeedContact {
    name: string;
    role?: string;
    email?: string;
    phone?: string;
}

// Anställningsform / omfattning / lön (#217)
export type EmploymentType = 'permanent' | 'temporary' | 'substitute' | 'probationary' | 'hourly';
export type EmploymentExtent = 'full_time' | 'part_time';
export type SalaryType = 'monthly' | 'hourly' | 'by_agreement';

export const EmploymentTypeLabels: Record<EmploymentType, string> = {
    permanent: 'Tillsvidare',
    temporary: 'Visstid',
    substitute: 'Vikariat',
    probationary: 'Provanställning',
    hourly: 'Timanställning',
};

export const EmploymentExtentLabels: Record<EmploymentExtent, string> = {
    full_time: 'Heltid',
    part_time: 'Deltid',
};

export interface Need {
    id: string;
    employer_id: string;
    unit_id: string;
    organisation_id: string;
    title: string;
    role: string;
    description?: string;
    body_markdown?: string;          // rich source-of-truth body (multi-section annons)
    onboarding_program_id?: string; // role-specific onboarding program (assigned on hire)
    competencies: string[];

    // ==================== 5-QUESTION MATCHING FIELDS ====================
    // Question 1: Hours per week
    min_hours_per_week: number;      // 1-40 hours
    max_hours_per_week: number;      // 1-40 hours

    // Question 2: Start date
    start_date: string;              // ISO format: "YYYY-MM-DD"

    // Question 3: City/Location
    city: string;                    // Where the job is located

    // Question 4: Work location type
    work_location_on_site: boolean;  // On-site work available
    work_location_remote: boolean;   // Remote work available

    // Question 5: Supervision
    offers_supervision: boolean;     // Employer offers supervision
    // ====================================================================

    // ==================== JOB POSTING DETAILS (#217) ====================
    application_deadline?: string;   // "YYYY-MM-DD" — sista ansökningsdag
    employment_type: EmploymentType; // anställningsform
    employment_extent: EmploymentExtent; // heltid/deltid
    extent_percent?: number;         // sysselsättningsgrad %, optional
    positions_count: number;         // antal tjänster
    contacts: NeedContact[];         // kontaktpersoner
    salary_min?: number;
    salary_max?: number;
    salary_type: SalaryType;
    // ====================================================================

    // Status
    status: NeedStatus;

    // Prerequisite courses a candidate must complete before applying (#40).
    // Populated by list endpoints only.
    required_courses?: { id: string; name: string }[];

    // #281 — application tally (employer list endpoint only). application_count
    // excludes withdrawn; undecided_count = pending + reviewing.
    application_count?: number;
    undecided_count?: number;

    published_at?: string;           // #281 — first time it went live (ISO)
    expired_at?: string;             // #281 — deadline auto-passed (ISO)
    deleted_at?: string;             // Null if not deleted
    created_at: string;
    updated_at: string;
}

export interface NeedListResponse {
    needs: Need[];
    total: number;
}

// ==================== STATUS ENUM ====================

export type NeedStatus = 'draft' | 'active' | 'paused' | 'filled' | 'closed' | 'expired';

export const NeedStatusLabels: Record<NeedStatus, string> = {
    draft: 'Utkast',
    active: 'Publicerad',
    paused: 'Pausad',
    filled: 'Tillsatt',
    closed: 'Stängd',
    expired: 'Utgången', // #281 — deadline passed, awaiting review
};

export const NeedStatusColors: Record<NeedStatus, string> = {
    draft: 'gray',
    active: 'green',
    paused: 'amber',
    filled: 'purple',
    closed: 'red',
    // #320 — 'expired' was added to NeedStatus and to the LABEL map (#281) but not
    // here, so an expired need drew its badge with an undefined colour.
    expired: 'amber'
};

// ==================== REQUEST TYPES ====================

// ✅ UPDATED: Added 5-question fields (all required)
export interface CreateNeedRequest {
    title: string;
    role: string;
    description?: string;
    onboarding_program_id?: string;
    competencies: string[];

    // 5 Questions (all required)
    min_hours_per_week: number;      // 1-40
    max_hours_per_week: number;      // 1-40
    start_date: string;              // "YYYY-MM-DD"
    city: string;
    work_location_on_site: boolean;
    work_location_remote: boolean;
    offers_supervision: boolean;

    // Job posting details (#217) — all optional; backend defaults apply
    application_deadline?: string;
    employment_type?: EmploymentType;
    employment_extent?: EmploymentExtent;
    extent_percent?: number;
    positions_count?: number;
    contacts?: NeedContact[];
    salary_min?: number;
    salary_max?: number;
    salary_type?: SalaryType;
}

// ✅ UPDATED: All fields optional for partial updates
export interface UpdateNeedRequest {
    title?: string;
    role?: string;
    description?: string;
    onboarding_program_id?: string;
    competencies?: string[];

    // 5 Questions (optional for updates)
    min_hours_per_week?: number;
    max_hours_per_week?: number;
    start_date?: string;
    city?: string;
    work_location_on_site?: boolean;
    work_location_remote?: boolean;
    offers_supervision?: boolean;

    // Status
    status?: NeedStatus;

    // Job posting details (#217)
    application_deadline?: string;
    employment_type?: EmploymentType;
    employment_extent?: EmploymentExtent;
    extent_percent?: number;
    positions_count?: number;
    contacts?: NeedContact[];
    salary_min?: number;
    salary_max?: number;
    salary_type?: SalaryType;
}

export interface UpdateNeedStatusRequest {
    status: NeedStatus;
}

// ==================== VALIDATION HELPERS ====================

/**
 * Validate hours per week range
 */
export const validateHoursPerWeek = (min: number, max: number): { valid: boolean; error?: string } => {
    if (min < 1 || min > 40) {
        return { valid: false, error: 'Minimitimmar måste vara mellan 1 och 40' };
    }
    if (max < 1 || max > 40) {
        return { valid: false, error: 'Maxtimmar måste vara mellan 1 och 40' };
    }
    if (min > max) {
        return { valid: false, error: 'Minimitimmar kan inte vara högre än maxtimmar' };
    }
    return { valid: true };
};

/**
 * Validate work location selection
 */
export const validateWorkLocation = (onSite: boolean, remote: boolean): { valid: boolean; error?: string } => {
    if (!onSite && !remote) {
        return { valid: false, error: 'Minst en arbetsplatstyp måste väljas (på plats eller distans)' };
    }
    return { valid: true };
};

/**
 * Validate start date
 */
export const validateStartDate = (startDate: string): { valid: boolean; error?: string } => {
    if (!startDate) {
        return { valid: false, error: 'Startdatum krävs' };
    }

    const date = new Date(startDate);
    if (isNaN(date.getTime())) {
        return { valid: false, error: 'Ogiltigt datumformat' };
    }

    return { valid: true };
};

/**
 * Validate competencies array
 */
export const validateCompetencies = (competencies: string[]): { valid: boolean; error?: string } => {
    if (!competencies || competencies.length === 0) {
        return { valid: false, error: 'Minst en kompetens krävs' };
    }

    if (competencies.some(c => !c || c.trim() === '')) {
        return { valid: false, error: 'Alla kompetenser måste ha ett värde' };
    }

    return { valid: true };
};

/**
 * Validate create need request
 */
export const validateCreateNeedRequest = (request: CreateNeedRequest): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Basic fields
    if (!request.title || request.title.trim() === '') {
        errors.push('Titel krävs');
    }

    if (!request.role || request.role.trim() === '') {
        errors.push('Roll krävs');
    }

    // Competencies
    const competencyValidation = validateCompetencies(request.competencies);
    if (!competencyValidation.valid) {
        errors.push(competencyValidation.error!);
    }

    // 5 Questions validation

    // Hours
    const hoursValidation = validateHoursPerWeek(request.min_hours_per_week, request.max_hours_per_week);
    if (!hoursValidation.valid) {
        errors.push(hoursValidation.error!);
    }

    // Date
    const dateValidation = validateStartDate(request.start_date);
    if (!dateValidation.valid) {
        errors.push(dateValidation.error!);
    }

    // City
    if (!request.city || request.city.trim() === '') {
        errors.push('Stad krävs');
    }

    // Work location
    const workLocValidation = validateWorkLocation(request.work_location_on_site, request.work_location_remote);
    if (!workLocValidation.valid) {
        errors.push(workLocValidation.error!);
    }

    return { valid: errors.length === 0, errors };
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Get need display title
 */
export const getNeedDisplayTitle = (need: Need): string => {
    return need.title;
};

/**
 * Get need status label in Swedish
 */
export const getNeedStatusLabel = (status: NeedStatus): string => {
    return NeedStatusLabels[status];
};

/**
 * Get need status color
 */
export const getNeedStatusColor = (status: NeedStatus): string => {
    return NeedStatusColors[status];
};

/**
 * Check if need is active (not deleted)
 */
export const isNeedActive = (need: Need): boolean => {
    return !need.deleted_at;
};

/**
 * Check if need can be edited
 */
export const canEditNeed = (need: Need): boolean => {
    return need.status === 'draft' || need.status === 'active' || need.status === 'paused';
};

/**
 * Check if need can be closed
 */
export const canCloseNeed = (need: Need): boolean => {
    return need.status !== 'filled' && need.status !== 'closed';
};

/**
 * Format start date for display
 */
export const formatStartDate = (startDate: string): string => {
    const date = new Date(startDate);
    return date.toLocaleDateString('sv-SE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

/**
 * Format work location display
 */
export const formatWorkLocationDisplay = (onSite: boolean, remote: boolean): string => {
    if (onSite && remote) return 'På plats & Distans (Hybrid)';
    if (onSite) return 'På plats';
    if (remote) return 'Distans';
    return 'Ej specificerad';
};

/**
 * Format hours range display
 */
export const formatHoursRangeDisplay = (min: number, max: number): string => {
    if (min === max) return `${min} timmar/vecka`;
    return `${min}-${max} timmar/vecka`;
};

// ==================== CANONICAL FORMATTERS (one set, used by NeedView) ====================

/** Short hours range, e.g. "20–30 tim/v". */
export const formatHoursRange = (min: number, max: number): string =>
    min === max ? `${min} tim/v` : `${min}–${max} tim/v`;

/** Canonical work-location label: "På plats" / "Distans" / "Hybrid". */
export const formatWorkLocation = (onSite: boolean, remote: boolean): string => {
    if (onSite && remote) return 'Hybrid';
    if (onSite) return 'På plats';
    if (remote) return 'Distans';
    return 'Ej angivet';
};

/** Anställningsform + omfattning, e.g. "Tillsvidare · Heltid" or "Vikariat · Deltid 75%". */
export const formatEmployment = (
    type?: EmploymentType,
    extent?: EmploymentExtent,
    extentPercent?: number,
): string => {
    const parts: string[] = [];
    if (type && EmploymentTypeLabels[type]) parts.push(EmploymentTypeLabels[type]);
    if (extent && EmploymentExtentLabels[extent]) {
        parts.push(extentPercent ? `${EmploymentExtentLabels[extent]} ${extentPercent}%` : EmploymentExtentLabels[extent]);
    }
    return parts.join(' · ');
};

/** Lön: range / single / "Enligt överenskommelse". */
export const formatSalary = (type?: SalaryType, min?: number, max?: number): string => {
    if (!type || type === 'by_agreement') return 'Enligt överenskommelse';
    const unit = type === 'hourly' ? 'kr/tim' : 'kr/mån';
    const fmt = (n: number) => n.toLocaleString('sv-SE');
    if (min && max) return min === max ? `${fmt(min)} ${unit}` : `${fmt(min)}–${fmt(max)} ${unit}`;
    if (min) return `Från ${fmt(min)} ${unit}`;
    if (max) return `Upp till ${fmt(max)} ${unit}`;
    return type === 'hourly' ? 'Timlön' : 'Månadslön';
};

/** Short date "1 sep 2026" for tiles/deadlines. */
export const formatShortDate = (date?: string): string => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * Create a duplicate request from an existing need (for client-side duplication)
 */
export const createDuplicateFromNeed = (need: Need, newStartDate?: string): CreateNeedRequest => {
    return {
        title: `${need.title} (Kopia)`,
        role: need.role,
        description: need.description,
        competencies: [...need.competencies],

        // 5 Questions
        min_hours_per_week: need.min_hours_per_week,
        max_hours_per_week: need.max_hours_per_week,
        start_date: newStartDate || need.start_date,
        city: need.city,
        work_location_on_site: need.work_location_on_site,
        work_location_remote: need.work_location_remote,
        offers_supervision: need.offers_supervision,
    };
};

/**
 * Check if need has complete 5-question data
 */
export const hasComplete5QuestionData = (need: Need): boolean => {
    return !!(
        need.min_hours_per_week &&
        need.max_hours_per_week &&
        need.start_date &&
        need.city &&
        (need.work_location_on_site !== undefined) &&
        (need.work_location_remote !== undefined) &&
        (need.offers_supervision !== undefined)
    );
};